/**
 * resolveArTableInsertAfterRowId
 *
 * The row a new row is inserted after — the one source of truth shared by
 * `ArTableControls`' "+ Add Row" button and its Signify insert handlers, so a
 * part fetched from the API lands where a typed row would (SYD-2373).
 *
 * The answer covers the ticked rows, the page demotion and the empty selection
 * together, because the three interact: an anchor that is the page's last row on
 * a non-final page is demoted one row up, or the new row ranks past the page
 * limit and the operator watches nothing appear.
 *
 * `whenNothingTicked` is the only difference between the two callers: "+ Add
 * Row" anchors on the page's last row, an API insert appends at the table's end.
 * Ticks that are all off the current page take that same branch, deliberately:
 * no painted row anchors them, and `clearSelection` on every page change makes
 * the state near-unreachable anyway.
 *
 * Reads the live selection and pagination, so callers resolve it at the moment
 * they act and capture the result before their next await.
 */

import type { ArRowId } from '#types/System.Artifacts.types';
import type { ArTableSelectionState } from '@/workflows/runtime/arTableSelection';

export function resolveArTableInsertAfterRowId(input: {
  selection: ArTableSelectionState;
  renderedRowIds: readonly ArRowId[];
  pagination: { currentPage: number; totalPages: number };
  whenNothingTicked: 'lastRenderedRow' | 'tableEnd';
}): ArRowId | undefined {
  const { selection, renderedRowIds, pagination, whenNothingTicked } = input;
  const lastIndex = renderedRowIds.length - 1;

  const tickedAnchor: ArRowId | undefined = (() => {
    if (selection.selectedCount === 1) return selection.selectedRowIds[0];

    if (selection.selectedCount > 1 && selection.selectedCount < renderedRowIds.length) {
      return [...renderedRowIds].reverse().find((rowId) => selection.selectedRows[rowId] === true);
    }

    /** Every row on the page is ticked: the new row goes above the last one, so the page still holds it. */
    if (selection.selectedCount > 0 && renderedRowIds.length >= 2) return renderedRowIds[lastIndex - 1];

    return undefined;
  })();

  const anchor = tickedAnchor
    ?? (whenNothingTicked === 'lastRenderedRow' && renderedRowIds.length > 0
      ? renderedRowIds[lastIndex]
      : undefined);

  if (anchor === undefined) return undefined;

  const wouldRankPastThePage =
    renderedRowIds.length > 1 &&
    pagination.currentPage < pagination.totalPages &&
    anchor === renderedRowIds[lastIndex];

  return wouldRankPastThePage ? renderedRowIds[lastIndex - 1] : anchor;
}
